import React, { useMemo, useState } from 'react';
import { StatusBadge } from './StatusBadge';
import {
  formatDate,
  formatDateTime,
  getOperationalBlockerLabel,
  getOperationalBlockerTone,
  getStoreLabel
} from '../utils/order';

const FILTERS = [
  ['ALL', 'All active overdue'],
  ['PURCHASE_ORDER_FULFILLMENT', 'Purchase order'],
  ['INVENTORY_PICKING', 'Committed, not picked'],
  ['INVENTORY_NOT_COMMITTED', 'Inventory not committed'],
  ['ORDER_HOLD_REASON_UNKNOWN', 'Order hold'],
  ['PICKED_NOT_SHIPPED_UNKNOWN', 'Picked, not shipped'],
  ['FULFILLMENT_PATH_NOT_ESTABLISHED', 'Fulfillment path not established'],
  ['BLOCKER_UNKNOWN', 'Blocker unknown']
];

export function OperationsRadar({ data, loading, error, onRefresh, onOpenOrder }) {
  const [filter, setFilter] = useState('ALL');

  const exceptions = data?.exceptions ?? [];
  const summary = data?.summary ?? {};

  const visible = useMemo(
    () => filter === 'ALL'
      ? exceptions
      : exceptions.filter((item) => item.primaryBlockerCode === filter),
    [exceptions, filter]
  );

  if (loading && !data) {
    return <div className="panel-loading large">Building operational exception radar…</div>;
  }

  if (error && !data) {
    return (
      <div className="order-error">
        <h2>Could not load Operations Radar</h2>
        <p>{error}</p>
        <button className="secondary-button" onClick={onRefresh}>Try again</button>
      </div>
    );
  }

  return (
    <div className="operations-radar">
      <section className="radar-hero">
        <div>
          <div className="eyebrow">Milestone 8 · Cross-order intelligence</div>
          <h1>Operational Exception Radar</h1>
          <p>Active overdue orders grouped by deterministic current-blocker evidence.</p>
        </div>
        <div className="radar-hero__actions">
          <span>Generated {formatDateTime(summary.generatedAtUtc)}</span>
          <button className="secondary-button" onClick={onRefresh} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh radar'}
          </button>
        </div>
      </section>

      <div className="radar-stat-grid">
        <RadarStat label="Active overdue" value={summary.activeOverdueCount ?? 0} emphasis />
        <RadarStat label="PO fulfillment" value={summary.purchaseOrderBlockerCount ?? 0} />
        <RadarStat label="Committed, not picked" value={summary.inventoryPickingBlockerCount ?? 0} />
        <RadarStat label="Inventory not committed" value={summary.inventoryNotCommittedCount ?? 0} />
        <RadarStat label="Order hold · reason unknown" value={summary.orderHoldReasonUnknownCount ?? 0} />
        <RadarStat label="Picked, not shipped" value={summary.pickedNotShippedUnknownCount ?? 0} />
        <RadarStat label="Fulfillment path not established" value={summary.fulfillmentPathNotEstablishedCount ?? 0} />
        <RadarStat label="Blocker unknown" value={summary.blockerUnknownCount ?? 0} />
        <RadarStat label="Oldest active delay" value={summary.oldestBusinessDaysOverdue ?? 0} suffix=" business days" />
      </div>

      <section className="radar-section-card">
        <div className="radar-section-heading">
          <div>
            <h2>Exception list</h2>
            <p>Showing {exceptions.length} of {summary.activeOverdueCount ?? exceptions.length} active-overdue exceptions. Top cards count the full radar population. Rank is for investigation order only, not customer or financial priority.</p>
          </div>
          {summary.multipleBlockerSignalCount > 0 ? (
            <StatusBadge tone="neutral">
              {summary.multipleBlockerSignalCount} multi-signal
            </StatusBadge>
          ) : null}
        </div>

        <div className="radar-filter-row">
          {FILTERS.map(([code, label]) => (
            <button
              key={code}
              className={filter === code ? 'active' : ''}
              onClick={() => setFilter(code)}
            >
              {label}
            </button>
          ))}
        </div>

        {visible.length ? (
          <div className="radar-table-wrap">
            <table className="radar-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Order</th>
                  <th>Expected</th>
                  <th>Overdue</th>
                  <th>Current blocker</th>
                  <th>Physical lines</th>
                  <th>Signals</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map((item) => {
                  const store = getStoreLabel({ order: { orderNo: item.orderNo } });
                  return (
                    <tr key={item.orderNo}>
                      <td className="radar-rank">#{item.attentionRank}</td>
                      <td>
                        <strong>{item.orderNo}</strong>
                        <span>{store || item.orderStatus || '—'}</span>
                      </td>
                      <td>{formatDate(item.expectedShipDate)}</td>
                      <td><strong>{item.businessDaysOverdue}</strong> business day(s)</td>
                      <td>
                        <StatusBadge tone={getOperationalBlockerTone(item.primaryBlockerCode)}>
                          {getOperationalBlockerLabel(item.primaryBlockerCode)}
                        </StatusBadge>
                        {item.unresolvedPONumbers ? (
                          <span className="radar-secondary">PO {item.unresolvedPONumbers}</span>
                        ) : null}
                      </td>
                      <td>{item.unshippedItemLines}/{item.totalItemLines} unshipped</td>
                      <td>{item.blockerSignalCount === 1 ? '1 signal' : `${item.blockerSignalCount} signals`}</td>
                      <td>
                        <button className="table-link-button" onClick={() => onOpenOrder(item.orderNo)}>
                          Investigate
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state compact">No exceptions match this blocker filter.</div>
        )}
      </section>

      <div className="radar-boundary-note">
        <strong>Deterministic boundary:</strong> the radar identifies current operational blocker signals from approved records. It does not claim historical root cause, vendor fault, staffing cause, or downstream stages that the source system does not record.
      </div>
    </div>
  );
}

function RadarStat({ label, value, suffix = '', emphasis = false }) {
  return (
    <div className={`radar-stat ${emphasis ? 'radar-stat--emphasis' : ''}`}>
      <span>{label}</span>
      <strong>{value}{suffix}</strong>
    </div>
  );
}
